import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  blogService,
  imageUploadService,
  MultilingualString,
} from "../../services/firebaseService";

const BlogForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title: { en: "", my: "" } as MultilingualString,
    content: { en: "", my: "" } as MultilingualString,
    excerpt: { en: "", my: "" } as MultilingualString,
    author: "",
    image: "",
    tags: [] as string[],
    published: false,
    featured: false,
    index: 0,
  });

  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");

  useEffect(() => {
    if (isEdit && id) {
      loadBlogPost(id);
    }
  }, [isEdit, id]);

  const loadBlogPost = async (blogId: string) => {
    try {
      setLoading(true);
      const blog = await blogService.getBlogPostById(blogId);
      if (blog) {
        // Convert to MultilingualString format if it's a string (backward compatibility)
        const normalizeMultilingual = (
          value: string | MultilingualString
        ): MultilingualString => {
          if (typeof value === "string") {
            return { en: value, my: "" };
          }
          return value;
        };

        setFormData({
          title: normalizeMultilingual(blog.title),
          content: normalizeMultilingual(blog.content),
          excerpt: normalizeMultilingual(blog.excerpt),
          author: blog.author,
          image: blog.image,
          tags: blog.tags,
          published: blog.published,
          featured: blog.featured,
          index: blog.index ?? 0,
        });
        setImagePreview(blog.image);
      }
    } catch (error) {
      console.error("Error loading blog post:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("Image size should be less than 5MB");
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview("");
    setFormData((prev) => ({ ...prev, image: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      let imageUrl = formData.image;

      // Upload image if a new file is selected
      if (imageFile) {
        setUploading(true);
        try {
          imageUrl = await imageUploadService.uploadImage(imageFile, "blogs");
          setFormData((prev) => ({ ...prev, image: imageUrl }));
        } catch (error) {
          console.error("Error uploading image:", error);
          alert("Failed to upload image. Please try again.");
          setUploading(false);
          setSaving(false);
          return;
        } finally {
          setUploading(false);
        }
      }

      // Update formData with the final image URL
      const finalFormData = { ...formData, image: imageUrl };

      if (isEdit && id) {
        await blogService.updateBlogPost(id, finalFormData);
      } else {
        await blogService.createBlogPost(finalFormData);
      }
      navigate("/admin");
    } catch (error) {
      console.error("Error saving blog post:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    // Handle multilingual fields (title.en, title.my, etc.)
    if (name.includes(".")) {
      const [field, lang] = name.split(".");
      if (field === "title" || field === "content" || field === "excerpt") {
        setFormData((prev) => ({
          ...prev,
          [field]: {
            ...(prev[field as keyof typeof prev] as MultilingualString),
            [lang]: value,
          },
        }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading blog post...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div>
          <h1>{isEdit ? "Edit Blog Post" : "Create New Blog Post"}</h1>
          <p>Fill in the details below</p>
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate("/admin")}
        >
          Back to Admin
        </button>
      </div>

      <div className="admin-content">
        <form onSubmit={handleSubmit} className="max-w-[800px] mx-auto">
          <div className="mb-6">
            <label className="block mb-2 font-medium text-gray-700">
              Title *
            </label>
            <div className="space-y-4">
              <div>
                <label className="block mb-1 text-sm text-gray-600">
                  English Title
                </label>
                <input
                  type="text"
                  name="title.en"
                  value={formData.title.en}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                  required
                  placeholder="Enter blog post title in English"
                />
              </div>
              <div>
                <label className="block mb-1 text-sm text-gray-600">
                  Myanmar Title (မြန်မာ)
                </label>
                <input
                  type="text"
                  name="title.my"
                  value={formData.title.my}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                  required
                  placeholder="Enter blog post title in Myanmar"
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block mb-2 font-medium text-gray-700">
              Excerpt *
            </label>
            <div className="space-y-4">
              <div>
                <label className="block mb-1 text-sm text-gray-600">
                  English Excerpt
                </label>
                <textarea
                  name="excerpt.en"
                  value={formData.excerpt.en}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 resize-y min-h-[100px]"
                  rows={3}
                  placeholder="Brief description in English"
                  required
                />
              </div>
              <div>
                <label className="block mb-1 text-sm text-gray-600">
                  Myanmar Excerpt (မြန်မာ)
                </label>
                <textarea
                  name="excerpt.my"
                  value={formData.excerpt.my}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 resize-y min-h-[100px]"
                  rows={3}
                  placeholder="Brief description in Myanmar"
                  required
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block mb-2 font-medium text-gray-700">
              Content *
            </label>
            <div className="space-y-4">
              <div>
                <label className="block mb-1 text-sm text-gray-600">
                  English Content
                </label>
                <textarea
                  name="content.en"
                  value={formData.content.en}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 resize-y min-h-[100px]"
                  rows={15}
                  placeholder="Write your blog post content in English..."
                  required
                />
              </div>
              <div>
                <label className="block mb-1 text-sm text-gray-600">
                  Myanmar Content (မြန်မာ)
                </label>
                <textarea
                  name="content.my"
                  value={formData.content.my}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 resize-y min-h-[100px]"
                  rows={15}
                  placeholder="Write your blog post content in Myanmar..."
                  required
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block mb-2 font-medium text-gray-700">
                Author *
              </label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleChange}
                className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                required
                placeholder="Author name"
              />
            </div>

            <div>
              <label className="block mb-2 font-medium text-gray-700">
                Image *
              </label>
              <input
                type="file"
                name="image"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full px-2 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm cursor-pointer transition-all duration-200 bg-gray-50 hover:border-primary hover:bg-blue-50/50 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                required={!isEdit || !formData.image}
              />
              {imagePreview && (
                <div className="mt-4 flex flex-col gap-2">
                  <div className="relative inline-block max-w-[300px] rounded-lg overflow-hidden border-2 border-gray-200">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-auto block max-h-[300px] object-contain bg-gray-50"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-xl leading-none p-0 shadow-lg bg-red-500 text-white hover:scale-110 transition-transform z-10"
                      title="Remove image"
                    >
                      ×
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 m-0">
                    {imageFile
                      ? `Selected: ${imageFile.name}`
                      : "Current image"}
                  </p>
                </div>
              )}
              {!imagePreview && !imageFile && isEdit && formData.image && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-900">
                  <p className="m-0">
                    Current image will be kept if no new image is selected.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="mb-6">
            <label className="block mb-2 font-medium text-gray-700">Tags</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                placeholder="Add a tag and press Enter"
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleAddTag}
              >
                Add Tag
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag, index) => (
                <span
                  key={index}
                  className="bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                >
                  {tag}
                  <button
                    type="button"
                    className="bg-transparent border-none text-gray-500 cursor-pointer text-base p-0 ml-1 hover:text-gray-700"
                    onClick={() => handleRemoveTag(tag)}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label
              htmlFor="index"
              className="block mb-2 font-medium text-gray-700"
            >
              Display Priority (Index)
            </label>
            <input
              type="number"
              id="index"
              name="index"
              value={formData.index}
              onChange={handleChange}
              className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              min="0"
              placeholder="0 = highest priority (appears first)"
            />
            <small className="block mt-2 text-[var(--text-secondary)] text-sm">
              Lower numbers appear first in the blog list. Posts with the same
              index are sorted by creation date.
            </small>
          </div>

          <div className="flex gap-8 my-6 md:flex-col md:gap-4">
            <label className="flex items-center gap-2 cursor-pointer font-medium text-gray-700">
              <input
                type="checkbox"
                name="published"
                checked={formData.published}
                onChange={handleChange}
              />
              Published
            </label>
            <label className="flex items-center gap-2 cursor-pointer font-medium text-gray-700">
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={handleChange}
              />
              Featured Post
            </label>
          </div>

          <div className="flex gap-4 justify-end mt-8 pt-4 border-t border-gray-200 md:flex-col">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate("/admin")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || uploading}
            >
              {uploading
                ? "Uploading image..."
                : saving
                ? "Saving..."
                : isEdit
                ? "Update Blog Post"
                : "Create Blog Post"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogForm;
